import React from 'react';
import { useAutoContinue } from '../hooks/useAutoContinue';

/** Check if error is an abort error (exported for unit testing). */
export function isAbortError(err: unknown): boolean {
  return (
    (err instanceof Error ||
      (typeof DOMException !== 'undefined' &&
        // eslint-disable-next-line no-undef
        err instanceof DOMException)) &&
    ((err as Error).name === 'AbortError' ||
      (err as Error).message === 'Request aborted')
  );
}

/** Minimal state slice needed for applyHandleNextStepError */
export interface HandleNextStepErrorStateSlice {
  getCurrentMessages: () => Array<unknown>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}

/**
 * Apply error from handleNextStep: version conflict is no-op; otherwise set
 * error only when there are no messages (to preserve last message on auto-continue failure).
 * Extracted for unit testing branch coverage.
 */
export function applyHandleNextStepError(
  err: unknown,
  state: HandleNextStepErrorStateSlice
): void {
  if (err instanceof Error && err.message.includes('Version conflict')) {
    console.error('[useLessonFlow] Version conflict:', err);
    return;
  }
  console.error('[useLessonFlow] Failed to advance lesson:', err);
  if (state.getCurrentMessages().length === 0) {
    state.setError(
      err instanceof Error ? err.message : 'Failed to advance lesson'
    );
  }
}

/** Minimal state slice needed for runAutoContinueWithErrorClearing */
export interface AutoContinueStateSlice {
  isAdvancingRef: React.MutableRefObject<boolean>;
  getCurrentMessages: () => Array<{ id: string }>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setProcessing: React.Dispatch<React.SetStateAction<boolean>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setAdvanceFailed: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Runs auto-continue (triggerAutoContinue) and on error optionally clears
 * error state when we had messages before (to preserve the last message).
 * Extracted for testability of the hadMessagesBeforeAutoContinue branch.
 *
 * A failed advance (handleNextStep returns false: it handles its own errors and
 * never rejects) flags `advanceFailed` so the learner is offered a retry
 * instead of being left on the last bubble (#250).
 */
export async function runAutoContinueWithErrorClearing(params: {
  runId: string;
  nextStepVersion: number;
  state: AutoContinueStateSlice;
  autoContinue: {
    triggerAutoContinue: ReturnType<
      typeof useAutoContinue
    >['triggerAutoContinue'];
  };
  handleNextStep: (runId: string, stepVersion: number) => Promise<boolean>;
  /** The run's abort signal; an advance aborted by a run change is not a failure. */
  signal?: AbortSignal;
  logLabel: string;
}): Promise<void> {
  const {
    runId,
    nextStepVersion,
    state,
    autoContinue,
    handleNextStep,
    signal,
    logLabel,
  } = params;

  state.isAdvancingRef.current = false;
  const hadMessagesBeforeAutoContinue = state.getCurrentMessages().length > 0;

  try {
    await autoContinue.triggerAutoContinue(
      async () => {
        if (
          !(await handleNextStep(runId, nextStepVersion)) &&
          !signal?.aborted
        ) {
          state.setAdvanceFailed(true);
        }
      },
      {
        setProcessing: state.setProcessing,
        setLoading: state.setLoading,
      }
    );
  } catch (err) {
    console.error(`[useLessonFlow] ${logLabel}`, err);
    state.isAdvancingRef.current = false;
    state.setAdvanceFailed(true);
    if (hadMessagesBeforeAutoContinue) {
      state.setError(null);
    }
  }
}
